import { BrandedFooter } from '@components/branding';
import FieldBuilder from '@components/field-builder';
import DeclineModal from '@containers/decline-modal';
import { ClientTrackingCategory } from '@models/client-tracking-category';
import { ClientTrackingPage } from '@models/client-tracking-page';
import { Field } from '@models/field-builder/field';
import { FieldType } from '@models/field-builder/field-type';
import { ReviewSection3Step } from '@models/remote-representative/review-section3-step';
import { ValidationErrorContainer } from '@screens/review-section3/review/validation-error-message';
import {
    CenterContainer,
    Container,
    Content,
    Header
} from '@screens/review-section3/styles';
import ConfigurationValuesService, { IConfigurationValuesService } from '@services/ConfigurationValuesService';
import {
    createDeclineModalStore,
    createLoaderService,
    createRemoteRepresentativeStore,
    createTrackingService,
    createTranslationService
} from '@services/factories';
import { ILoaderService } from '@services/LoaderService';
import { StringFormatService } from '@services/StringFormatService';
import { ITrackingService } from '@services/TrackingService';
import { IDeclineModalStore } from '@stores/modal/decline-modal-store';
import { IRemoteRepresentativeStore } from '@stores/remote-representative/remote-representative-store';
import { ScrollContainer } from '@styles/container-styles';
import { ExternalLink } from '@styles/shared-styles';
import { cacheTranslations } from '@utils/cache-translations';
import {
    computed,
    Lambda,
    observe
} from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import DocumentDetails from './document-details';
import EmployeeDetails from './employee-details';
import Step4Details from './step4-details';
import {
    AgreementTitle,
    ButtonContainer,
    CompletionContainer,
    CompletionIcon,
    CompletionText,
    DeclineButton,
    ESignButton,
    InstructionLine,
    ReviewPageContainer,
    RoundedContainer,
    Title
} from './styles';

enum ReviewTranslation {
    instructionsFor = 'instructionsFor',
    step3Title = 'step3Title',
    instructionsLine2 = 'instructionsLine2',
    instructionsLine3 = 'instructionsLine3',
    section1Title = 'section1Title',
    section2Title = 'section2Title',
    step4Title = 'step4Title',
    eSignatureText = 'eSignatureText',
    agreement1 = 'agreement1',
    agreement2 = 'agreement2',
    agreement3 = 'agreement3',
    buttonDecline = 'buttonDecline',
    buttonSign = 'buttonSign',
    thanksText = 'thanksText',
    eSignValidationErrorMessage = 'eSignValidationErrorMessage',
    requestFailure = 'requestFailure',
    declineSection3Context = 'declineSection3Context'
}

class Review extends React.Component<RouteComponentProps> {
    // Probably good idea to dependency inject these later
    private configurationValuesService: IConfigurationValuesService;
    remoteRepresentativeStore: IRemoteRepresentativeStore = createRemoteRepresentativeStore();
    loaderService: ILoaderService = createLoaderService();
    trackingService: ITrackingService = createTrackingService();
    declineModalStore: IDeclineModalStore = createDeclineModalStore();

    private readonly translations = cacheTranslations(
        createTranslationService(),
        ReviewTranslation
    );

    private completionPageViewUnsubscriber: Lambda | null = null;

    @computed
    private get agreementCheckField(): Field[] {
        return [
            {
                type: FieldType.CHECKBOX,
                id: 'agreement-check',
                label: <><p>{this.translations.agreement1}</p><p>{this.translations.agreement2}</p>
                    <p>{this.translations.agreement3}</p></>,
                width: 250,
                setValueMethod: this.remoteRepresentativeStore.setESignAgreement,
                value: this.remoteRepresentativeStore.eSignAgreement
            }
        ];
    }

    constructor(props) {
        super(props);
        this.configurationValuesService = ConfigurationValuesService.getInstance();
    }

    async componentDidMount() {
        this.loaderService.showLoader();
        await this.remoteRepresentativeStore.initialise();
        this.loaderService.hideLoader();

        this.completionPageViewUnsubscriber = observe(
            this.remoteRepresentativeStore,
            'currentStep',
            change => {
            if (change.newValue === ReviewSection3Step.COMPLETION) {
                // as this page is an inner state of the Review page and not routed
                // the page view must be triggered manually instead of the PageViewTracker component
                this.trackingService.trackPageView(ClientTrackingPage.Section3ThankYou);
            }
        })
    }

    componentWillUnmount() {
        this.completionPageViewUnsubscriber?.();
    }

    renderInstructionsFor = () => {
        const employerName = this.remoteRepresentativeStore.representativeName || '';
        return (<InstructionLine>
            {StringFormatService.format(this.translations.instructionsFor, [employerName])}
        </InstructionLine>);
    };

    renderUSCISWebSiteLink = () => <ExternalLink href={this.configurationValuesService.uscisWebsiteUrl}
                                                 target='_blank'>{this.configurationValuesService.uscisWebsiteUrl}</ExternalLink>;
    renderUSCISSection3Link = () => <ExternalLink href={this.configurationValuesService.uscisSection3FormI9Url}
                                                  target='_blank'>{this.configurationValuesService.uscisSection3FormI9Url}</ExternalLink>;

    renderValidationError = () => !this.remoteRepresentativeStore.eSignIsFilled ?
        <ValidationErrorContainer>
            {this.translations.eSignValidationErrorMessage}
        </ValidationErrorContainer> : null;

    renderRequestError = () => this.remoteRepresentativeStore.hadErrorESigning ?
        <ValidationErrorContainer>
            {this.translations.requestFailure}
        </ValidationErrorContainer> : null;

    renderReviewPage = () => {
        const validationError = this.renderValidationError();
        const requestError = this.renderRequestError();
        return (
            <ReviewPageContainer>
                <Title>{this.translations.section1Title}</Title>
                <EmployeeDetails store={this.remoteRepresentativeStore}/>
                <Title topSpacing={true}>{this.translations.section2Title}</Title>
                <DocumentDetails store={this.remoteRepresentativeStore}/>
                <Title topSpacing={true}>{this.translations.step4Title}</Title>
                <Step4Details store={this.remoteRepresentativeStore}/>
                <RoundedContainer>
                    <AgreementTitle>{this.translations.eSignatureText}</AgreementTitle>
                    <FieldBuilder fields={this.agreementCheckField}/>
                </RoundedContainer>
                {validationError}
                {requestError}
                <ButtonContainer>
                    <DeclineButton onClick={this.declineModalStore.openModal}>
                        {this.translations.buttonDecline}
                    </DeclineButton>
                    <ESignButton onClick={this.remoteRepresentativeStore.requestESignAsync}>
                        {this.translations.buttonSign}
                    </ESignButton>
                </ButtonContainer>
            </ReviewPageContainer>
        );
    };

    renderCompletionPage = () => {
        return (
            <CompletionContainer>
                <div><CompletionIcon fontSize={'large'}/></div>
                <CompletionText>{this.translations.thanksText}</CompletionText>
            </CompletionContainer>
        );
    };

    renderPageContent(tabKey: ReviewSection3Step): React.ReactNode | null {

        window.scrollTo(0, 0);

        const contentMap: {
            [key: string]: () => React.ReactNode
        } = {
            [ReviewSection3Step.REVIEW]: () => this.renderReviewPage(),
            [ReviewSection3Step.COMPLETION]: () => this.renderCompletionPage()
        };

        const contentRenderer = contentMap[tabKey];

        if (!contentRenderer) {
            console.error('unrecognised Review Step', tabKey);
            return null;
        }

        return contentRenderer();
    }

    render() {
        const instructionsFor = this.renderInstructionsFor();
        const uscisWebsiteLink = this.renderUSCISWebSiteLink();
        const uscisSection3Link = this.renderUSCISSection3Link();
        const pageContent = this.renderPageContent(this.remoteRepresentativeStore.currentStep);

        return (
            <ScrollContainer>
                <CenterContainer fillAvailable={true}>
                    <Container>
                        <Header>
                            {instructionsFor}
                            <Title topSpacing={true}
                                   bottomSpacing={true}>{this.translations.step3Title}</Title>
                            <InstructionLine>{this.translations.instructionsLine2}{uscisWebsiteLink}</InstructionLine>
                            <InstructionLine>{this.translations.instructionsLine3}{uscisSection3Link}</InstructionLine>
                        </Header>
                        <Content>
                            {pageContent}
                        </Content>
                    </Container>
                </CenterContainer>
                <DeclineModal store={this.declineModalStore}
                              declineQuestionContext={this.translations.declineSection3Context}
                              onDeclineSuccess={this.remoteRepresentativeStore.goToCompletionStep}
                              trackingCategory={ClientTrackingCategory.Section3Decline} />
                <BrandedFooter />
            </ScrollContainer>
        );
    }
}

export default observer(Review);



// WEBPACK FOOTER //
// ./src/screens/review-section3/review/index.tsx